import { useTranslation } from 'react-i18next'
import type { Conflict } from '@/services/merge'
import type { Choice, Resolution } from '@/services/merge/resolve'
import { FieldDiff } from './FieldDiff'
import { OrderDiff } from './OrderDiff'

interface ConflictRowProps {
  conflict: Conflict
  resolution: Resolution | undefined
  onChoose: (conflictId: string, choice: Choice) => void
  onFieldChoose: (conflictId: string, field: string, choice: Choice) => void
}

export function ConflictRow({ conflict, resolution, onChoose, onFieldChoose }: ConflictRowProps) {
  const { t } = useTranslation()
  const currentChoice = resolution?.choice ?? conflict.suggested
  const fieldChoices = resolution?.fieldChoices

  return (
    <div
      className="rounded-lg border p-3 space-y-1"
      style={{
        borderColor: resolution ? 'var(--border)' : 'color-mix(in srgb, var(--color-warning, #f59e0b) 40%, var(--border))',
        background: 'var(--bg-card)',
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <ConflictKindBadge kind={conflict.conflictKind} />
        <span className="text-[12px] font-medium truncate">{conflict.label}</span>
      </div>

      {/* Body */}
      {conflict.conflictKind === 'field' && (
        <FieldDiff
          conflict={conflict}
          currentChoice={currentChoice}
          fieldChoices={fieldChoices}
          onChoose={(c) => onChoose(conflict.id, c)}
          onFieldChoose={(f, c) => onFieldChoose(conflict.id, f, c)}
        />
      )}

      {conflict.conflictKind === 'delete-modify' && (
        <DeleteModifyRow
          conflict={conflict}
          currentChoice={currentChoice}
          onChoose={(c) => onChoose(conflict.id, c)}
        />
      )}

      {conflict.conflictKind === 'order' && (
        <OrderDiff
          mineOrder={(conflict.mineSnapshot as string[]) ?? []}
          theirsOrder={(conflict.theirsSnapshot as string[]) ?? []}
          currentChoice={currentChoice}
          onChoose={(c) => onChoose(conflict.id, c)}
        />
      )}
    </div>
  )
}

function DeleteModifyRow({
  conflict,
  currentChoice,
  onChoose,
}: {
  conflict: Conflict
  currentChoice: Choice
  onChoose: (c: Choice) => void
}) {
  const { t } = useTranslation()
  const mineDeleted = conflict.mineSnapshot === undefined || conflict.mineSnapshot === null
  const theirsDeleted = conflict.theirsSnapshot === undefined || conflict.theirsSnapshot === null

  return (
    <div className="flex gap-2 mt-1.5">
      <DeleteModifyOption
        label={mineDeleted ? t('conflict.deleteModifyRemove', 'remover') : t('conflict.deleteModifyKeep', 'manter')}
        sublabel={t('conflict.mine', 'o meu')}
        chosen={currentChoice === 'mine'}
        onChoose={() => onChoose('mine')}
        testId="delete-modify-mine"
      />
      <DeleteModifyOption
        label={theirsDeleted ? t('conflict.deleteModifyRemove', 'remover') : t('conflict.deleteModifyKeep', 'manter')}
        sublabel={t('conflict.theirs', 'o deles')}
        chosen={currentChoice === 'theirs'}
        onChoose={() => onChoose('theirs')}
        testId="delete-modify-theirs"
      />
    </div>
  )
}

function DeleteModifyOption({
  label,
  sublabel,
  chosen,
  onChoose,
  testId,
}: {
  label: string
  sublabel: string
  chosen: boolean
  onChoose: () => void
  testId?: string
}) {
  return (
    <button
      type="button"
      onClick={onChoose}
      data-testid={testId}
      className="flex-1 rounded-lg border px-3 py-2 text-left text-[11px] transition-colors"
      style={{
        borderColor: chosen ? 'var(--accent)' : 'var(--border)',
        background: chosen ? 'var(--accent-muted, color-mix(in srgb, var(--accent) 10%, transparent))' : 'var(--bg-card)',
        color: chosen ? 'var(--text)' : 'var(--text-dim)',
        opacity: chosen ? 1 : 0.65,
      }}
    >
      <div className="font-medium">{label}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{sublabel}</div>
    </button>
  )
}

function ConflictKindBadge({ kind }: { kind: Conflict['conflictKind'] }) {
  const { t } = useTranslation()
  const labels: Record<Conflict['conflictKind'], string> = {
    field: t('conflict.kindField', 'campos'),
    'delete-modify': t('conflict.kindDeleteModify', 'delete/modify'),
    order: t('conflict.kindOrder', 'ordem'),
  }
  const colors: Record<Conflict['conflictKind'], string> = {
    field: 'var(--accent)',
    'delete-modify': 'var(--color-danger, #ef4444)',
    order: 'var(--color-warning, #f59e0b)',
  }
  return (
    <span
      className="text-[9px] font-semibold uppercase rounded px-1.5 py-0.5 flex-shrink-0"
      style={{
        background: `color-mix(in srgb, ${colors[kind]} 15%, transparent)`,
        color: colors[kind],
        border: `1px solid color-mix(in srgb, ${colors[kind]} 35%, transparent)`,
      }}
    >
      {labels[kind]}
    </span>
  )
}
